import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Col, Form, Modal, Pagination, Row, Spinner, Table } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { useAuth } from '../../context/AuthProvider';
import { qcSessionAPI } from '../../services';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { Calendar, Plus, Eye, Edit, Trash2, CheckCircle, Play, LogOut } from 'lucide-react';

const PAGE_SIZE = 20;

const SESSION_TYPE_LABELS = {
  AUTO: 'Tự động',
  MANUAL: 'Thủ công',
};

const SESSION_STATUS_LABELS = {
  OPEN: 'Chờ nhận',
  IN_PROGRESS: 'Đang soi',
  COMPLETED: 'Đã xong',
};

const SESSION_STATUS_VARIANTS = {
  OPEN: 'warning',
  IN_PROGRESS: 'primary',
  COMPLETED: 'success',
};

const formatDate = (value) => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'dd/MM/yyyy');
  } catch {
    return value;
  }
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'dd/MM HH:mm');
  } catch {
    return value;
  }
};

/**
 * Tổng số mục của phiên — phải cộng CẢ 4 NGUỒN.
 * Bản cũ chỉ đọc `_count.shift_tasks` nên phiên 20 mục mà đa số là chuẩn bị bàn /
 * kiểm game / loyalty sẽ hiển thị con số bé xíu, trông như phiên rỗng.
 */
const totalItems = (session) =>
  (session?._count?.shift_tasks || 0) +
  (session?._count?.prep_reservations || 0) +
  (session?._count?.game_check_sessions || 0) +
  (session?._count?.loyalty_participations || 0);

const QcSessionManagementPage = () => {
  const navigate = useNavigate();
  const { branches, selectedBranch } = useBranch();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, total_pages: 1, claim_ttl_hours: null });
  const [page, setPage] = useState(1);
  const [backlog, setBacklog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [createForm, setCreateForm] = useState({
    branch_id: selectedBranch?.id || '',
    planned_task_date: format(new Date(), 'yyyy-MM-dd'),
    review_date_from: format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    review_date_to: format(new Date(), 'yyyy-MM-dd'),
    items_target: 20,
  });
  const [editForm, setEditForm] = useState(null);

  const isAdminOrManager = useMemo(() => {
    return user?.role === 'ADMIN' || user?.role === 'MANAGER';
  }, [user]);

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        branch_id: selectedBranch?.id,
        type: filters.type || undefined,
        status: filters.status || undefined,
        page,
        limit: PAGE_SIZE,
      };
      const response = await qcSessionAPI.getQcSessions(params);
      const list = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      setSessions(list);
      setMeta(response?.meta || { total: list.length, page, total_pages: 1, claim_ttl_hours: null });
    } catch (error) {
      console.error('Cannot fetch QC sessions', error);
      toast.error(error?.message || 'Không tải được danh sách phiên');
    } finally {
      setLoading(false);
    }
  }, [filters, selectedBranch, page]);

  /** Backlog = việc đang chờ QC. Không có số này thì không biết QC phủ được bao nhiêu. */
  const fetchBacklog = useCallback(async () => {
    try {
      const response = await qcSessionAPI.getQcBacklog();
      setBacklog(response || null);
    } catch (error) {
      console.error('Cannot fetch QC backlog', error);
      setBacklog(null);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    fetchBacklog();
  }, [fetchBacklog]);

  // Đổi bộ lọc / chi nhánh → về trang 1, tránh ở lại trang trống
  useEffect(() => {
    setPage(1);
  }, [filters.type, filters.status, selectedBranch?.id]);

  useEffect(() => {
    if (selectedBranch?.id) {
      setCreateForm((prev) => ({ ...prev, branch_id: selectedBranch.id }));
    }
  }, [selectedBranch]);

  const summary = useMemo(() => {
    const inProgress = sessions.filter((s) => s.status === 'IN_PROGRESS').length;
    const completed = sessions.filter((s) => s.status === 'COMPLETED').length;
    const waiting = sessions.filter((s) => s.status === 'OPEN').length;
    return { total: meta.total ?? sessions.length, inProgress, completed, waiting };
  }, [sessions, meta.total]);

  const backlogForBranch = useMemo(() => {
    if (!backlog?.branches) return null;
    if (selectedBranch?.id) {
      return backlog.branches.find((b) => b.branch_id === selectedBranch.id) || null;
    }
    return null;
  }, [backlog, selectedBranch]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchSessions(), fetchBacklog()]);
  }, [fetchSessions, fetchBacklog]);

  const handleCreateSession = async () => {
    try {
      if (!createForm.branch_id) {
        toast.error('Vui lòng chọn chi nhánh');
        return;
      }
      setLoading(true);
      await qcSessionAPI.createQcSession(createForm);
      toast.success('Tạo phiên thành công');
      setShowCreateModal(false);
      await refreshAll();
    } catch (error) {
      console.error('Cannot create session', error);
      toast.error(error?.message || 'Không thể tạo phiên');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinSession = async (sessionId) => {
    try {
      setLoading(true);
      await qcSessionAPI.joinQcSession(sessionId);
      toast.success('Đã nhận phiên');
      await refreshAll();
    } catch (error) {
      console.error('Cannot join session', error);
      toast.error(error?.message || 'Không thể nhận phiên');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSession = async (sessionId) => {
    if (!window.confirm('Bạn có chắc muốn hoàn thành phiên này?')) return;
    try {
      setLoading(true);
      await qcSessionAPI.completeQcSession(sessionId);
      toast.success('Đã hoàn thành phiên');
      await refreshAll();
    } catch (error) {
      console.error('Cannot complete session', error);
      toast.error(error?.message || 'Không thể hoàn thành phiên');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveSession = async (sessionId) => {
    if (
      !window.confirm(
        'Thu hồi phiên này? Các mục chưa chấm sẽ quay lại hàng chờ cho người khác. Phiên chưa chấm mục nào sẽ bị xoá.'
      )
    ) {
      return;
    }
    try {
      setLoading(true);
      const result = await qcSessionAPI.leaveQcSession(sessionId);
      const released = result?.released || {};
      const count =
        (released.shift_tasks || 0) +
        (released.game_check_sessions || 0) +
        (released.loyalty_participations || 0) +
        (released.table_preps || 0);
      toast.success(`Đã thu hồi phiên, trả ${count} mục về hàng chờ`);
      await refreshAll();
    } catch (error) {
      console.error('Cannot leave session', error);
      toast.error(error?.message || 'Không thể thu hồi phiên');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSession = (session) => {
    setEditForm({
      planned_task_date: session.planned_task_date,
      review_date_from: session.review_date_from,
      review_date_to: session.review_date_to,
      items_target: session.items_target || 20,
    });
    setSelectedSession(session);
    setShowEditModal(true);
  };

  const handleUpdateSession = async () => {
    if (!selectedSession) return;
    try {
      setLoading(true);
      await qcSessionAPI.updateQcSession(selectedSession.id, editForm);
      toast.success('Cập nhật phiên thành công');
      setShowEditModal(false);
      setSelectedSession(null);
      await refreshAll();
    } catch (error) {
      console.error('Cannot update session', error);
      toast.error(error?.message || 'Không thể cập nhật phiên');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Bạn có chắc muốn xóa phiên này? Hành động này không thể hoàn tác.')) return;
    try {
      setLoading(true);
      await qcSessionAPI.deleteQcSession(sessionId);
      toast.success('Đã xóa phiên');
      await refreshAll();
    } catch (error) {
      console.error('Cannot delete session', error);
      toast.error(error?.message || 'Không thể xóa phiên');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = meta.total_pages || 1;

  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header my-2">
          <div className="mc-page-header-title">
            <h1>Phiên QC</h1>
            <small className="text-muted">
              Phiên tự động sinh khi nhân viên nhận nhiệm vụ QC — mỗi phiên là một gói việc cỡ cố
              định, lấy từ hàng chờ gần nhất.
            </small>
          </div>
          <div className="mc-page-header-actions d-flex gap-2">
            {isAdminOrManager && (
              <Button
                variant="primary"
                onClick={() => setShowCreateModal(true)}
                disabled={loading}
              >
                <Plus
                  size={18}
                  className="me-1"
                />
                Tạo phiên thủ công
              </Button>
            )}
            <Button
              variant="outline-secondary"
              onClick={refreshAll}
              disabled={loading}
            >
              {loading ? 'Đang tải...' : 'Làm mới'}
            </Button>
          </div>
        </div>

        <Row className="mb-4">
          <Col
            md={3}
            className="mb-3"
          >
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <small className="text-muted text-uppercase">Tổng phiên</small>
                <h3 className="mb-0">{summary.total}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col
            md={3}
            className="mb-3"
          >
            <Card className="h-100 shadow-sm border-primary">
              <Card.Body>
                <small className="text-muted text-uppercase">Đang soi (trang này)</small>
                <h3 className="text-primary mb-0">{summary.inProgress}</h3>
                {meta.claim_ttl_hours != null && (
                  <small className="text-muted">Tự thu hồi sau {meta.claim_ttl_hours}h</small>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col
            md={3}
            className="mb-3"
          >
            <Card className="h-100 shadow-sm border-success">
              <Card.Body>
                <small className="text-muted text-uppercase">Đã xong (trang này)</small>
                <h3 className="text-success mb-0">{summary.completed}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col
            md={3}
            className="mb-3"
          >
            <Card className="h-100 shadow-sm border-warning">
              <Card.Body>
                <small className="text-muted text-uppercase">Việc đang chờ QC</small>
                <h3 className="text-warning mb-0">
                  {backlogForBranch
                    ? backlogForBranch.pending_items
                    : (backlog?.total_pending ?? '-')}
                </h3>
                <small className="text-muted">
                  {backlogForBranch
                    ? `${backlogForBranch.completed_sessions} phiên đã soi / ${backlog?.days_back ?? 30} ngày`
                    : 'Tất cả chi nhánh'}
                </small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Độ phủ QC: còn tồn bao nhiêu so với đã soi — số này trước đây không có ở đâu */}
        {backlog?.branches?.length > 0 && (
          <Card className="mb-4 shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Độ phủ QC ({backlog.days_back} ngày gần nhất)</h5>
              <small className="text-muted">
                &quot;Đang chờ&quot; là số mục đủ điều kiện QC mà chưa phiên nào lấy. Mục cũ hơn cửa
                sổ lấy việc sẽ không được soi nữa.
              </small>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table
                  size="sm"
                  className="mb-0"
                >
                  <thead>
                    <tr>
                      <th>Chi nhánh</th>
                      <th className="text-end">Đang chờ QC</th>
                      <th className="text-end">Phiên đã xong</th>
                      <th className="text-end" title="Số phiên đã đóng và chấm đủ ngưỡng mục — chỉ những phiên này được tính vào đánh giá nhân viên (#180900)">
                        Phiên đủ set
                      </th>
                      <th className="text-end">Mục đã soi</th>
                      <th className="text-end">Mục / phiên</th>
                      <th className="text-end">Cửa sổ lấy việc</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backlog.branches.map((b) => (
                      <tr key={b.branch_id}>
                        <td>{b.branch_name}</td>
                        <td className="text-end fw-semibold">{b.pending_items}</td>
                        <td className="text-end">{b.completed_sessions}</td>
                        <td className="text-end">
                          {b.evaluation_sessions}
                          <span className="text-muted"> / {b.min_items_for_evaluation} mục</span>
                        </td>
                        <td className="text-end">{b.reviewed_items}</td>
                        <td className="text-end">{b.items_per_session}</td>
                        <td className="text-end">{b.lookback_days} ngày</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        <Card className="mb-4 shadow-sm">
          <Card.Header>
            <h5 className="mb-0">Bộ lọc</h5>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col lg={4}>
                <Form.Label>Loại</Form.Label>
                <Form.Select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  <option value="">Tất cả</option>
                  <option value="AUTO">Tự động</option>
                  <option value="MANUAL">Thủ công</option>
                </Form.Select>
              </Col>
              <Col lg={4}>
                <Form.Label>Trạng thái</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">Tất cả</option>
                  <option value="IN_PROGRESS">Đang soi</option>
                  <option value="COMPLETED">Đã xong</option>
                  {/* OPEN chỉ còn ở phiên thủ công admin tạo mà chưa ai nhận */}
                  <option value="OPEN">Chờ nhận (thủ công)</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="shadow-sm">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">Danh sách phiên</h5>
              <small className="text-muted">
                {loading
                  ? 'Đang tải...'
                  : `${sessions.length} / ${meta.total ?? sessions.length} phiên — trang ${meta.page ?? page}/${totalPages}`}
              </small>
            </div>
            {loading && (
              <Spinner
                animation="border"
                size="sm"
              />
            )}
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table
                hover
                className="mb-0"
              >
                <thead>
                  <tr>
                    <th>Chi nhánh</th>
                    <th>Loại</th>
                    <th>Trạng thái</th>
                    <th>Ngày phiên</th>
                    <th>Cửa sổ lấy việc</th>
                    <th>QC viên</th>
                    <th>Số mục</th>
                    <th>Phân bổ theo nguồn</th>
                    <th>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && sessions.length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        className="text-center py-4 text-muted"
                      >
                        Không có phiên nào theo bộ lọc hiện tại.
                      </td>
                    </tr>
                  )}
                  {sessions.map((session) => {
                    const items = totalItems(session);
                    const target = session.items_target || 0;
                    return (
                      <tr key={session.id}>
                        <td>{session.branch?.name || session.branch_id || '-'}</td>
                        <td>
                          <Badge bg={session.type === 'AUTO' ? 'info' : 'secondary'}>
                            {SESSION_TYPE_LABELS[session.type] || session.type}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={SESSION_STATUS_VARIANTS[session.status] || 'secondary'}>
                            {SESSION_STATUS_LABELS[session.status] || session.status}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-1">
                            <Calendar
                              size={14}
                              className="text-muted"
                            />
                            {formatDate(session.planned_task_date)}
                          </div>
                          {session.claimed_at && (
                            <small className="text-muted">
                              Nhận {formatDateTime(session.claimed_at)}
                            </small>
                          )}
                        </td>
                        <td>
                          <small>
                            {formatDate(session.review_date_from)} → {formatDate(session.review_date_to)}
                          </small>
                        </td>
                        <td>{session.qc_employee?.full_name || '-'}</td>
                        <td>
                          <span className="fw-semibold">{items}</span>
                          {target > 0 && <small className="text-muted"> / {target}</small>}
                        </td>
                        <td>
                          <small className="text-muted">
                            Task {session._count?.shift_tasks || 0} · Bàn{' '}
                            {session._count?.prep_reservations || 0} · Game{' '}
                            {session._count?.game_check_sessions || 0} · Loyalty{' '}
                            {session._count?.loyalty_participations || 0}
                          </small>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => navigate(`/qc-sessions/${session.id}`)}
                              title="Xem chi tiết"
                            >
                              <Eye size={14} />
                            </Button>
                            {session.status === 'OPEN' && !session.qc_employee_id && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleJoinSession(session.id)}
                                disabled={loading}
                                title="Nhận phiên"
                              >
                                <Play size={14} />
                              </Button>
                            )}
                            {session.status === 'OPEN' && items === 0 && isAdminOrManager && (
                              <>
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => handleEditSession(session)}
                                  disabled={loading}
                                  title="Sửa phiên"
                                >
                                  <Edit size={14} />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteSession(session.id)}
                                  disabled={loading}
                                  title="Xóa phiên"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </>
                            )}
                            {session.status === 'IN_PROGRESS' && isAdminOrManager && (
                              <>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handleCompleteSession(session.id)}
                                  disabled={loading}
                                  title="Hoàn thành phiên"
                                >
                                  <CheckCircle size={14} />
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => handleLeaveSession(session.id)}
                                  disabled={loading}
                                  title="Thu hồi phiên, trả mục chưa chấm về hàng chờ"
                                >
                                  <LogOut size={14} />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </Card.Body>
          {totalPages > 1 && (
            <Card.Footer className="d-flex justify-content-center">
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                />
                <Pagination.Item active>
                  {page} / {totalPages}
                </Pagination.Item>
                <Pagination.Next
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                />
              </Pagination>
            </Card.Footer>
          )}
        </Card>
      </div>

      {/* Modal tạo phiên thủ công */}
      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Tạo phiên QC thủ công</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Phiên thủ công nằm chờ cho tới khi có nhân viên nhận nhiệm vụ QC — người đó sẽ nhận phiên
            này trước các phiên tự sinh. Dùng khi cần soi đúng một khoảng ngày cụ thể.
          </p>
          <Form>
            <Row className="g-3">
              <Col md={12}>
                <Form.Label>Chi nhánh *</Form.Label>
                <Form.Select
                  value={createForm.branch_id}
                  onChange={(e) => setCreateForm({ ...createForm, branch_id: e.target.value })}
                  required
                >
                  <option value="">Chọn chi nhánh</option>
                  {branches.map((branch) => (
                    <option
                      key={branch.id}
                      value={branch.id}
                    >
                      {branch.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Ngày phiên *</Form.Label>
                <Form.Control
                  type="date"
                  value={createForm.planned_task_date}
                  onChange={(e) => setCreateForm({ ...createForm, planned_task_date: e.target.value })}
                  required
                />
              </Col>
              <Col md={6}>
                <Form.Label>Tổng số mục *</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={createForm.items_target}
                  onChange={(e) => setCreateForm({ ...createForm, items_target: Number(e.target.value) })}
                  required
                />
                <Form.Text muted>Gộp cả 4 nguồn: task ca, chuẩn bị bàn, kiểm game, loyalty.</Form.Text>
              </Col>
              <Col md={6}>
                <Form.Label>Lấy việc từ ngày *</Form.Label>
                <Form.Control
                  type="date"
                  value={createForm.review_date_from}
                  onChange={(e) => setCreateForm({ ...createForm, review_date_from: e.target.value })}
                  max={createForm.review_date_to}
                  required
                />
              </Col>
              <Col md={6}>
                <Form.Label>Lấy việc tới ngày *</Form.Label>
                <Form.Control
                  type="date"
                  value={createForm.review_date_to}
                  onChange={(e) => setCreateForm({ ...createForm, review_date_to: e.target.value })}
                  min={createForm.review_date_from}
                  required
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCreateModal(false)}
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateSession}
            disabled={loading}
          >
            {loading ? 'Đang tạo...' : 'Tạo phiên'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal sửa phiên */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedSession(null);
          setEditForm(null);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Sửa phiên QC</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editForm && (
            <Form>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Ngày phiên *</Form.Label>
                  <Form.Control
                    type="date"
                    value={editForm.planned_task_date}
                    onChange={(e) => setEditForm({ ...editForm, planned_task_date: e.target.value })}
                    required
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Tổng số mục *</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={editForm.items_target}
                    onChange={(e) => setEditForm({ ...editForm, items_target: Number(e.target.value) })}
                    required
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Lấy việc từ ngày *</Form.Label>
                  <Form.Control
                    type="date"
                    value={editForm.review_date_from}
                    onChange={(e) => setEditForm({ ...editForm, review_date_from: e.target.value })}
                    max={editForm.review_date_to}
                    required
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Lấy việc tới ngày *</Form.Label>
                  <Form.Control
                    type="date"
                    value={editForm.review_date_to}
                    onChange={(e) => setEditForm({ ...editForm, review_date_to: e.target.value })}
                    min={editForm.review_date_from}
                    required
                  />
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowEditModal(false);
              setSelectedSession(null);
              setEditForm(null);
            }}
          >
            Hủy
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateSession}
            disabled={loading}
          >
            {loading ? 'Đang cập nhật...' : 'Cập nhật phiên'}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
};

export default QcSessionManagementPage;
